import { RadioGroup, FormControlLabel, Radio, Button } from "@mui/material";
import { useTranslations } from "use-intl";

import { CabinClass } from "@/app/lib/types/enums";
import type { TFlightFormFieldValues } from "@/app/lib/types/types";
import { MAX_TRAVELLERS } from "@/app/lib/constants";
import PlusMinusButton from "../CounterButton";
import { Controller, useForm, useWatch } from "react-hook-form";

type Props = {
  initialValues: TFlightFormFieldValues;
  onConfirm: (values: TFlightFormFieldValues) => void;
};

function PassengersSelection({ initialValues, onConfirm }: Props) {
  const t = useTranslations();

  const { control, handleSubmit } = useForm({
    defaultValues: initialValues,
  });

  const [adult, child] = useWatch({ control, name: ["adult", "child"] });

  const maximumChild = MAX_TRAVELLERS - adult;
  const maximumAdult = MAX_TRAVELLERS - child;

  return (
    <form
      onSubmit={handleSubmit(onConfirm)}
      className="flex h-full w-full flex-col"
    >
      <span className="mb-4 inline-block text-xs text-text-tertiary">
        {t("flights.search.travellers")}
      </span>
      <div className="flex justify-between py-2" aria-label="Adult">
        <div className="flex flex-col">
          <h4 className="font-medium text-text-primary">
            {t("flights.search.adult")}
          </h4>
          <span className="inline-block text-xs font-normal text-text-tertiary">
            {t("flights.search.twelveYearOldPlus")}
          </span>
        </div>
        <Controller
          name="adult"
          control={control}
          render={({ field }) => (
            <PlusMinusButton {...field} minValue={1} maxValue={maximumAdult} />
          )}
        />
      </div>
      <div className="flex justify-between py-2" aria-label="Child">
        <div className="flex flex-col">
          <h4 className="font-medium text-text-primary">
            {t("flights.search.child")}
          </h4>
          <span className="inline-block text-xs font-normal text-text-tertiary">
            {t("flights.search.twoToTwelveYearOld")}
          </span>
        </div>
        <Controller
          name="child"
          control={control}
          render={({ field }) => (
            <PlusMinusButton
              {...field}
              value={field.value}
              maxValue={maximumChild}
            />
          )}
        />
      </div>
      <div className="flex justify-between py-2" aria-label="Infant">
        <div className="flex flex-col">
          <h4 className="font-medium text-text-primary">
            {t("flights.search.infant")}
          </h4>
          <span className="inline-block max-w-[200px] text-xs font-normal text-wrap text-text-tertiary">
            {t("flights.search.withoutSeat")}
          </span>
        </div>
        <Controller
          name="infantWithoutSeat"
          control={control}
          render={({ field }) => (
            <PlusMinusButton {...field} maxValue={adult} />
          )}
        />
      </div>
      <span className="mt-4 mb-2 inline-block text-xs text-text-tertiary">
        {t("flights.cabinClass.cabinClass")}
      </span>
      <div>
        <Controller
          control={control}
          name="cabinClass"
          render={({ field }) => (
            <RadioGroup
              aria-labelledby="cabin-class-radio-group"
              defaultValue="economy"
              {...field}
            >
              <FormControlLabel
                className="flex justify-between py-2"
                value={CabinClass.ANY}
                control={<Radio />}
                label={t("flights.cabinClass.any")}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
              <FormControlLabel
                className="flex justify-between py-2"
                value={CabinClass.ECONOMY}
                control={<Radio />}
                label={t("flights.cabinClass.economy")}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
              <FormControlLabel
                className="m-0 flex justify-between py-2"
                value={CabinClass.PREMIUM_ECONOMY}
                control={<Radio />}
                label={t("flights.cabinClass.premiumEconomy")}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
              <FormControlLabel
                className="m-0 flex justify-between py-2"
                value={CabinClass.FIRST_CLASS}
                control={<Radio />}
                label={t("flights.cabinClass.firstClass")}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
              <FormControlLabel
                className="m-0 flex justify-between py-2"
                value={CabinClass.BUSINESS}
                control={<Radio />}
                label={t("flights.cabinClass.business")}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
            </RadioGroup>
          )}
        />
      </div>
      <Button
        variant="contained"
        size="medium"
        type="submit"
        fullWidth
        sx={{
          marginTop: "auto",
        }}
      >
        {t("confirm")}
      </Button>
    </form>
  );
}

export default PassengersSelection;
